import { normalizeText } from '@vif/core';
import JSZip from 'jszip';
import { DomUtils, parseDocument } from 'htmlparser2';
import { ParserError } from './errors.js';
import { assertByteLimit, finalizeDocument } from './limits.js';
import { ZipExpansionBudget, assertStructureCount, assertZipEntryCount, readZipEntryBytes, readZipEntryText, } from './zip.js';
/**
 * EPUB parser — ports the prototype's behavior (reference/SpeedReaderv2.html
 * parseEpub) onto htmlparser2 (no DOMParser: RN-safe per PRD §8.5), with one
 * upgrade from the P1 brief: chapter titles prefer the nav.xhtml / NCX table
 * of contents, falling back to the prototype's first-h1–h4 heuristic.
 * Footnote-ish noise inside chapter bodies is tolerated (cut line).
 */
/** Prototype block-level extraction set. */
const BLOCK_TAGS = new Set(['p', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'li', 'blockquote', 'div']);
const STRIPPED_TAGS = new Set(['script', 'style', 'svg', 'img', 'figure']);
const HEADING_TAGS = ['h1', 'h2', 'h3', 'h4'];
const MAX_CHAPTER_TITLE_CHARS = 200;
/** Prototype thresholds: fall back to whole-body text under 20 chars; skip
 * spine docs with under 10 chars of text. */
const MIN_BLOCKS_TEXT_CHARS = 20;
const MIN_DOC_TEXT_CHARS = 10;
/**
 * Open the zip and locate + parse the OPF. Throws the same broken-EPUB
 * ParserErrors parseEpub has always thrown; extractEpubCover wraps this in a
 * try/catch and degrades to null instead of surfacing them.
 */
async function openEpub(data, budget, options) {
    assertByteLimit(data.byteLength);
    let zip;
    try {
        zip = await JSZip.loadAsync(data);
    }
    catch {
        throw new ParserError('corrupt-file');
    }
    // M-03: bound the declared entry count before touching any entry, and route
    // every entry read below through the budgeted incremental reader.
    assertZipEntryCount(Object.keys(zip.files).length, budget.limits);
    const container = zip.file('META-INF/container.xml');
    if (container === null)
        throw new ParserError('corrupt-file', 'Invalid EPUB: missing container.xml');
    const containerDoc = parseDocument(await readZipEntryText(container, budget, options), {
        xmlMode: true,
    });
    const rootfile = findFirst(containerDoc, (el) => tagName(el) === 'rootfile');
    const rootfilePath = rootfile?.attribs['full-path'];
    if (rootfilePath === undefined)
        throw new ParserError('corrupt-file', 'Invalid EPUB: no rootfile');
    const opfFile = zip.file(rootfilePath);
    if (opfFile === null)
        throw new ParserError('corrupt-file', 'Invalid EPUB: OPF file not found');
    const opfDir = rootfilePath.includes('/')
        ? rootfilePath.slice(0, rootfilePath.lastIndexOf('/') + 1)
        : '';
    const opfDoc = parseDocument(await readZipEntryText(opfFile, budget, options), {
        xmlMode: true,
    });
    return { zip, opfDoc, opfDir };
}
/**
 * N-01: entry lookup by (possibly percent-encoded) href. A malformed
 * percent-escape used to escape parseEpub as a raw URIError from
 * decodeURIComponent; the typed contract is total, so it maps to corrupt-file.
 * Decoding and fallback-to-raw behavior are otherwise unchanged.
 */
function fileForHref(zip, path) {
    let decoded;
    try {
        decoded = decodeURIComponent(path);
    }
    catch {
        throw new ParserError('corrupt-file', 'Malformed percent-escape in EPUB reference.');
    }
    return zip.file(decoded) ?? zip.file(path);
}
/** OPF <manifest> as id → item. Shared by the content parse and cover lookup. */
function buildManifest(opfDoc) {
    const manifest = new Map();
    for (const item of findAll(opfDoc, (el) => tagName(el) === 'item')) {
        const id = item.attribs['id'];
        const href = item.attribs['href'];
        if (id !== undefined && href !== undefined) {
            manifest.set(id, {
                href,
                mediaType: item.attribs['media-type'] ?? '',
                properties: item.attribs['properties'] ?? '',
            });
        }
    }
    return manifest;
}
export async function parseEpub(data, options) {
    const budget = new ZipExpansionBudget();
    const { zip, opfDoc, opfDir } = await openEpub(data, budget, options);
    const manifest = buildManifest(opfDoc);
    assertStructureCount(manifest.size, 'manifest', budget.limits);
    const spineEl = findFirst(opfDoc, (el) => tagName(el) === 'spine');
    const spineIds = [];
    for (const itemref of findAll(opfDoc, (el) => tagName(el) === 'itemref')) {
        const idref = itemref.attribs['idref'];
        if (idref !== undefined && manifest.has(idref))
            spineIds.push(idref);
    }
    assertStructureCount(spineIds.length, 'spine', budget.limits);
    const titles = await chapterTitles(zip, opfDir, manifest, spineEl?.attribs['toc'], budget, options);
    const blocks = [];
    for (const id of spineIds) {
        if (options?.signal?.aborted === true) {
            throw Object.assign(new Error('cancelled'), { name: 'AbortError' });
        }
        const item = manifest.get(id);
        if (item.mediaType !== '' && !/xhtml|html/.test(item.mediaType))
            continue;
        const path = resolveZipPath(opfDir, item.href);
        const file = fileForHref(zip, path);
        if (file === null)
            continue;
        const dom = parseDocument(await readZipEntryText(file, budget, options));
        const bodyBlocks = extractBodyBlocks(dom);
        const bodyText = bodyBlocks.join(' ');
        if (bodyText.length < MIN_DOC_TEXT_CHARS)
            continue;
        // Titles and body text are both NFC-normalized at extraction.
        const navTitle = titles.get(stripFragment(path));
        const headingTitle = firstHeadingText(dom);
        const title = navTitle ?? headingTitle;
        if (title !== undefined) {
            blocks.push({ kind: 'chapter-heading', text: title, title, level: 1 });
        }
        let chapterOpened = title !== undefined;
        for (const text of bodyBlocks) {
            // The heading text already opens the chapter; don't read it twice.
            if (chapterOpened && text === title) {
                chapterOpened = false;
                continue;
            }
            blocks.push({ kind: 'paragraph', text });
        }
    }
    if (blocks.length === 0) {
        throw new ParserError('empty-document', 'Could not extract readable text from this EPUB.');
    }
    const doc = finalizeDocument(blocks);
    const metadata = extractEpubMetadata(opfDoc);
    return metadata === undefined ? doc : { ...doc, metadata };
}
/**
 * Cover image bytes straight out of the EPUB zip — never a bare zip ref, so the
 * app writes bytes to disk at import and never reopens the archive. Resolves
 * the EPUB 3 manifest properties="cover-image" item first, then the EPUB 2
 * <meta name="cover"> → manifest id fallback. Returns null (never throws) when
 * there is no cover, the reference dangles, or the referenced file is absent.
 */
export async function extractEpubCover(data) {
    try {
        const budget = new ZipExpansionBudget();
        const { zip, opfDoc, opfDir } = await openEpub(data, budget);
        const manifest = buildManifest(opfDoc);
        const coverItem = resolveCoverItem(opfDoc, manifest);
        if (coverItem === undefined)
            return null;
        // GATE A — the manifest media-type must be present and one RN Image can
        // render. Absent, blank, or anything else (incl. image/svg+xml, which RN
        // Image cannot render) degrades to no-cover.
        const mediaType = coverItem.mediaType;
        if (!COVER_MEDIA_TYPES.has(mediaType))
            return null;
        // Strict resolution: a cover href that climbs out of the archive root (or is
        // absolute) resolves to undefined → no cover, rather than clamping at root
        // and silently picking up an unrelated same-named entry.
        const path = resolveZipPathStrict(opfDir, coverItem.href);
        if (path === undefined)
            return null;
        const file = fileForHref(zip, path);
        if (file === null)
            return null;
        // GATE B — the bytes must be a well-formed image of the DECLARED type. The
        // manifest is authoritative: we never re-type from sniffing, so a mislabeled
        // (or truncated) cover fails closed to no-cover.
        const bytes = await readZipEntryBytes(file, budget);
        if (!coverBytesMatchDeclaredType(mediaType, bytes))
            return null;
        return { bytes, mediaType };
    }
    catch {
        return null;
    }
}
/**
 * OPF Dublin Core metadata — best-effort across EPUB 2 and 3. Missing or blank
 * dc:title / dc:creator / dc:language come back undefined; a fully-empty result
 * collapses to undefined so callers read "no metadata" as absence. The first
 * dc:creator wins (file-as / role refinements ignored for v1). Never throws —
 * a malformed OPF just yields sparse metadata.
 */
function extractEpubMetadata(opfDoc) {
    let metadataEl;
    try {
        metadataEl = findFirst(opfDoc, (el) => tagName(el) === 'metadata');
    }
    catch {
        return undefined;
    }
    if (metadataEl === null)
        return undefined;
    const readDc = (local) => {
        const el = findFirst(metadataEl, (e) => localName(e) === local);
        if (el === null)
            return undefined;
        const text = normalizeText(DomUtils.textContent(el).replace(/\s+/g, ' ').trim());
        return text.length > 0 ? text : undefined;
    };
    const title = readDc('title');
    const author = readDc('creator');
    const language = readDc('language');
    if (title === undefined && author === undefined && language === undefined)
        return undefined;
    return { title, author, language };
}
/** Cover manifest item: EPUB 3 properties="cover-image" preferred, else the
 * EPUB 2 <meta name="cover"> content=id → manifest lookup. */
function resolveCoverItem(opfDoc, manifest) {
    for (const item of manifest.values()) {
        if (item.properties.split(/\s+/).includes('cover-image'))
            return item;
    }
    const meta = findFirst(opfDoc, (el) => tagName(el) === 'meta' && el.attribs['name'] === 'cover');
    const coverId = meta?.attribs['content'];
    if (coverId !== undefined)
        return manifest.get(coverId);
    return undefined;
}
/** GATE A allowlist — the image media-types RN Image can render. image/svg+xml
 * is deliberately absent: RN Image cannot draw SVG, so an SVG cover is refused. */
const COVER_MEDIA_TYPES = new Set(['image/png', 'image/jpeg', 'image/gif', 'image/webp']);
/** True when bytes[offset..] equals `expected`, bounds-guarded: a negative or
 * past-the-end offset is a non-match, never an out-of-range read. */
function bytesMatchAt(bytes, offset, expected) {
    if (offset < 0 || offset + expected.length > bytes.byteLength)
        return false;
    for (let i = 0; i < expected.length; i += 1) {
        if (bytes[offset + i] !== expected[i])
            return false;
    }
    return true;
}
/**
 * GATE B — the entry must be a well-formed image of the DECLARED type. Each
 * format checks its start signature AND an end marker, so a truncated file
 * fails. The manifest is authoritative: a PNG-declared entry is only ever
 * checked against PNG (never re-typed from sniffing), so mislabeled covers fail
 * closed even when the bytes would validate as a different format.
 */
function coverBytesMatchDeclaredType(mediaType, bytes) {
    const end = bytes.byteLength;
    switch (mediaType) {
        case 'image/png':
            // 8-byte signature + the fixed 12-byte IEND chunk: its data is empty so
            // its CRC is constant, making the trailer a precise truncation check.
            return (bytesMatchAt(bytes, 0, [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a]) &&
                bytesMatchAt(bytes, end - 12, [0x00, 0x00, 0x00, 0x00, 0x49, 0x45, 0x4e, 0x44, 0xae, 0x42, 0x60, 0x82]));
        case 'image/jpeg':
            return (bytesMatchAt(bytes, 0, [0xff, 0xd8, 0xff]) && bytesMatchAt(bytes, end - 2, [0xff, 0xd9]));
        case 'image/gif':
            // "GIF87a" or "GIF89a" header, GIF trailer byte 0x3B.
            return ((bytesMatchAt(bytes, 0, [0x47, 0x49, 0x46, 0x38, 0x37, 0x61]) ||
                bytesMatchAt(bytes, 0, [0x47, 0x49, 0x46, 0x38, 0x39, 0x61])) &&
                bytesMatchAt(bytes, end - 1, [0x3b]));
        case 'image/webp': {
            // "RIFF" container tagged "WEBP"; the LE u32 at 4..7 counts the bytes
            // after the 8-byte RIFF header, so byteLength should equal riffSize + 8
            // (a single trailing pad byte for odd-sized payloads is tolerated).
            if (!bytesMatchAt(bytes, 0, [0x52, 0x49, 0x46, 0x46]) ||
                !bytesMatchAt(bytes, 8, [0x57, 0x45, 0x42, 0x50])) {
                return false;
            }
            // The RIFF/WEBP match above guarantees bytes 4..7 exist; the undefined
            // guard keeps TS's noUncheckedIndexedAccess happy without an assertion.
            const b4 = bytes[4];
            const b5 = bytes[5];
            const b6 = bytes[6];
            const b7 = bytes[7];
            if (b4 === undefined || b5 === undefined || b6 === undefined || b7 === undefined)
                return false;
            const riffSize = (b4 | (b5 << 8) | (b6 << 16) | (b7 << 24)) >>> 0;
            const slack = end - (riffSize + 8);
            return slack === 0 || slack === 1;
        }
        default:
            return false;
    }
}
/** Element name with any namespace prefix stripped (dc:title → title). */
function localName(el) {
    const name = tagName(el);
    const colon = name.indexOf(':');
    return colon === -1 ? name : name.slice(colon + 1);
}
/** nav.xhtml (EPUB 3) or NCX (EPUB 2) → map of zip path (no fragment) → title. */
async function chapterTitles(zip, opfDir, manifest, spineTocId, budget, options) {
    const titles = new Map();
    const addTitle = (docDir, href, title) => {
        if (href === undefined || href.startsWith('#') === true)
            return;
        const clean = normalizeText(title.replace(/\s+/g, ' ').trim());
        if (clean.length === 0 || clean.length >= MAX_CHAPTER_TITLE_CHARS)
            return;
        const path = stripFragment(resolveZipPath(docDir, href));
        if (!titles.has(path))
            titles.set(path, clean);
    };
    for (const item of manifest.values()) {
        if (!item.properties.split(/\s+/).includes('nav'))
            continue;
        const navPath = resolveZipPath(opfDir, item.href);
        const file = fileForHref(zip, navPath);
        if (file === null)
            break;
        const dom = parseDocument(await readZipEntryText(file, budget, options));
        const navDir = dirOf(navPath);
        // Prefer the nav[epub:type=toc] section; fall back to the whole doc.
        const tocNav = findFirst(dom, (el) => tagName(el) === 'nav' && el.attribs['epub:type'] === 'toc') ?? dom;
        for (const anchor of findAll(tocNav, (el) => tagName(el) === 'a')) {
            addTitle(navDir, anchor.attribs['href'], DomUtils.textContent(anchor));
        }
        if (titles.size > 0)
            return titles;
        break;
    }
    // NCX fallback: spine@toc id, else any x-dtbncx manifest item.
    let ncxHref;
    if (spineTocId !== undefined)
        ncxHref = manifest.get(spineTocId)?.href;
    if (ncxHref === undefined) {
        for (const item of manifest.values()) {
            if (item.mediaType.includes('dtbncx')) {
                ncxHref = item.href;
                break;
            }
        }
    }
    if (ncxHref !== undefined) {
        const ncxPath = resolveZipPath(opfDir, ncxHref);
        const file = fileForHref(zip, ncxPath);
        if (file !== null) {
            const dom = parseDocument(await readZipEntryText(file, budget, options), { xmlMode: true });
            const ncxDir = dirOf(ncxPath);
            // tagName() lowercases, so match the lowercased NCX element names.
            for (const navPoint of findAll(dom, (el) => tagName(el) === 'navpoint')) {
                const label = findFirst(navPoint, (el) => tagName(el) === 'text');
                const content = findFirst(navPoint, (el) => tagName(el) === 'content');
                if (label !== null && content !== null) {
                    addTitle(ncxDir, content.attribs['src'], DomUtils.textContent(label));
                }
            }
        }
    }
    return titles;
}
/** Containers with one of these as a descendant are skipped — their text is
 * collected from the descendants instead (prototype rule). */
const CONTAINER_MARKER_TAGS = new Set(['p', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6']);
/** Prototype extraction: block elements without nested p/h*, deduped by
 * text; whole-body fallback when almost nothing was collected. */
function extractBodyBlocks(dom) {
    const body = findFirst(dom, (el) => tagName(el) === 'body') ?? dom;
    const texts = [];
    const seen = new Set();
    for (const el of findAll(body, (node) => BLOCK_TAGS.has(tagName(node)))) {
        if (findFirst(el, (child) => CONTAINER_MARKER_TAGS.has(tagName(child))) !== null)
            continue;
        const text = cleanText(el);
        if (text.length > 0 && !seen.has(text)) {
            seen.add(text);
            texts.push(text);
        }
    }
    if (texts.join(' ').length < MIN_BLOCKS_TEXT_CHARS) {
        const whole = cleanText(body);
        return whole.length > 0 ? [whole] : [];
    }
    return texts;
}
function firstHeadingText(dom) {
    const heading = findFirst(dom, (el) => HEADING_TAGS.includes(tagName(el)));
    if (heading === null)
        return undefined;
    const text = cleanText(heading);
    return text.length > 0 && text.length < MAX_CHAPTER_TITLE_CHARS ? text : undefined;
}
/** textContent minus stripped subtrees, whitespace-collapsed and
 * NFC-normalized at the boundary (soul.md §7) so every downstream
 * comparison and emitted block sees composed text. */
function cleanText(node) {
    let out = '';
    const walk = (current) => {
        if (current.type === 'text') {
            out += current.data;
            return;
        }
        if (current.type === 'tag' || current.type === 'script' || current.type === 'style') {
            const element = current;
            if (STRIPPED_TAGS.has(element.name.toLowerCase()))
                return;
            for (const child of element.children)
                walk(child);
        }
    };
    const children = 'children' in node ? node.children : [];
    for (const child of children)
        walk(child);
    return normalizeText(out.replace(/\s+/g, ' ').trim());
}
function tagName(el) {
    return el.name.toLowerCase();
}
function findFirst(root, match) {
    return DomUtils.findOne((el) => match(el), ('children' in root ? root.children : [root]), true);
}
function findAll(root, match) {
    return DomUtils.findAll((el) => match(el), ('children' in root ? root.children : [root]));
}
function resolveZipPath(dir, href) {
    const joined = dir + href;
    const parts = [];
    for (const part of joined.split('/')) {
        if (part === '' || part === '.')
            continue;
        if (part === '..')
            parts.pop();
        else
            parts.push(part);
    }
    return parts.join('/');
}
/**
 * Like resolveZipPath, but REJECTS references that leave the archive root
 * instead of clamping them there. Decoding behavior is otherwise identical (the
 * caller still percent-decodes the result). Used only for cover resolution: a
 * `..` with nothing left to pop, or a leading-slash (absolute) href, returns
 * undefined so extractEpubCover degrades to no-cover rather than clamping to
 * root and resolving an unrelated entry. resolveZipPath keeps its clamping
 * semantics for the spine/nav/NCX callers, whose pre-refactor error contract
 * must be preserved.
 */
function resolveZipPathStrict(dir, href) {
    if (href.startsWith('/'))
        return undefined;
    const parts = [];
    for (const part of (dir + href).split('/')) {
        if (part === '' || part === '.')
            continue;
        if (part === '..') {
            if (parts.length === 0)
                return undefined;
            parts.pop();
        }
        else {
            parts.push(part);
        }
    }
    return parts.join('/');
}
function stripFragment(path) {
    const hash = path.indexOf('#');
    return hash === -1 ? path : path.slice(0, hash);
}
function dirOf(path) {
    return path.includes('/') ? path.slice(0, path.lastIndexOf('/') + 1) : '';
}
//# sourceMappingURL=epub.js.map